// Token-level streaming smoothing (SILL-459).
//
// Streamed assistant text arrives in bursty `text_delta` frames — the network
// delivers chunks of wildly varying size at irregular intervals, so painting
// each frame as it lands makes the reply jump in visible blocks. This module is
// the pure core of a render-buffer: state holds the full received text (truth),
// and the reveal advances a prefix toward it at a steady per-tick cadence.
//
// The cadence must drain the buffer *slightly slower than* text arrives, so the
// buffer stays non-empty and motion is CONTINUOUS. If it drained faster than
// arrival it would empty between network bursts and stall — revealing a block,
// waiting, revealing the next block (the "blocky / buffering" feel). So each
// tick reveals a small, character-level step: a low floor keeps a trickle
// gliding, a gentle proportional term (long time-constant) lets it trail the
// stream by ~half a second and catch up on a big backlog, and a ceiling stops
// any single tick from dumping a whole paragraph.

/** Interval between reveal ticks (~60fps). */
export const REVEAL_INTERVAL_MS = 16;

/** Floor so a tiny trickle still glides (~60 chars/sec at the tick rate). */
export const MIN_CHARS_PER_TICK = 1;

/**
 * Reveal ~1/N of the remaining backlog each tick. Larger N = longer
 * time-constant = the reveal trails further behind the stream and flows more
 * smoothly (N * REVEAL_INTERVAL_MS ≈ the drain half-life, here ~0.48s).
 */
export const CATCH_UP_DIVISOR = 30;

/** Ceiling so a huge instant dump never reveals a whole block in one tick. */
export const MAX_CHARS_PER_TICK = 24;

/**
 * Given the currently-revealed character count and the full target text,
 * return the next revealed count for this tick. Character-level, monotonic,
 * bounded by `MIN`/`MAX` per tick, and never exceeds `target.length`.
 */
export function nextRevealCount(current: number, target: string): number {
  const targetLen = target.length;
  if (current >= targetLen) return targetLen;

  const backlog = targetLen - current;
  const step = Math.min(
    MAX_CHARS_PER_TICK,
    Math.max(MIN_CHARS_PER_TICK, Math.round(backlog / CATCH_UP_DIVISOR)),
  );
  return Math.min(targetLen, current + step);
}
